"use client";

import React from "react";
import { ArrowRight, ShieldCheck, HardHat, Users, Clock, Building2, Hammer, Factory, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const corePillars = [{
  icon: Clock,
  title: "Program reliability",
  description: "Programmed handovers that keep your build schedules and follow-on trades moving without bottleneck delays."
}, {
  icon: ShieldCheck,
  title: "Verified compliance",
  description: "NASC and CHAS accredited working methods with full TG20:21 design and inspection compliance."
}, {
  icon: Users,
  title: "Multi-trade coordination",
  description: "Adaptable deck heights and clear access corridors that integrate smoothly with bricklayers, carpenters, and roofers."
}, {
  icon: HardHat,
  title: "Dedicated site oversight",
  description: "Experienced supervision on every phase to manage adjustments, inspections, and handover sign-offs promptly."
}];
const contractorServices = [{
  title: "Commercial scaffolding",
  description: "Full-height access, birdcages, and perimeter scaffolds for commercial builds, fit-outs, and multi-storey projects.",
  to: "/CommercialScaffoldingPage",
  icon: Building2
}, {
  title: "New build scaffolding",
  description: "Progressive lifts, loading bays, and fall prevention designed specifically for residential and mixed-use developments.",
  to: "/NewBuildScaffoldingPage",
  icon: Hammer
}, {
  title: "Refurbishment scaffolding",
  description: "Carefully planned facade access, cantilevered towers, and temporary protection for active structural renovations.",
  to: "/RefurbishmentScaffoldingPage",
  icon: HardHat
}, {
  title: "Industrial scaffolding",
  description: "Heavy-duty loading capacities, plant access solutions, and specialist containment systems for industrial facilities.",
  to: "/IndustrialScaffoldingPage",
  icon: Factory
}];
const coverageAreas = ["Berkshire", "Surrey", "Hampshire", "Buckinghamshire", "London", "Home Counties"];
const accreditations = ["NASC", "CHAS"];
export default function ContractorOverview() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  return <section id="maincontractoroverview" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header & Answer-First Overview */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs">
              Commercial & Development
            </Badge>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <MapPin className="size-3.5 text-primary" />
              <span>South East coverage</span>
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 font-default">
            Scaffolding for main contractors & developers
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
            Main contractors and developers need dependable coordination on ongoing builds, strict safety compliance, clear communication, and the practical capability to work seamlessly alongside other trades. BMF Scaffolding Ltd delivers scheduled phased erections, prompt adaptations, and rigorous site supervision across the South East.
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button asChild size="lg" className="h-11 px-6">
              <Link to="/ContactRequestQuotePage">
                Request contractor tender
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="h-11 px-6">
              <Link to="/HealthSafetyAccreditationsPage">View accreditations</Link>
            </Button>
          </div>
        </div>

        {/* Accreditations Trust Bar */}
        <div className="rounded-xl bg-card border border-border p-4 sm:p-6 mb-16 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Verified compliance standards
              </p>
              <p className="text-sm font-medium text-foreground">
                Working safely with full insurance, RAMS, and certified standards
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {accreditations.map((item, i) => <div key={item} data-index={i} className="flex items-center gap-1.5 rounded-md bg-muted px-3 py-1.5 text-xs font-semibold text-foreground">
                  <CheckCircle2 className="size-3.5 text-primary" />
                  <span>{item}</span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Core Pillars Grid */}
        <div className="mb-20">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground font-default">
              How BMF supports your project delivery
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Structured to handle the commercial, logistical, and safety demands of principal contractors.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {corePillars.map((pillar, i) => {
            const IconComponent = pillar.icon;
            return <motion.div key={pillar.title} variants={fadeIn} data-index={i}>
                  <Card className="h-full border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow]">
                    <CardHeader className="gap-3">
                      <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <IconComponent className="size-5" />
                      </div>
                      <CardTitle className="text-lg font-semibold text-card-foreground">
                        {pillar.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-normal text-muted-foreground">
                        {pillar.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Relevant Services Grid */}
        <div className="mb-20">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
            <div>
              <h2 className="text-2xl font-bold text-foreground font-default">
                Relevant services for commercial contracts
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Access packages tailored to project scale, structural demands, and site logistics.
              </p>
            </div>
            <Button asChild variant="link" className="px-0 self-start sm:self-auto">
              <Link to="/ProjectsCaseStudiesPage">
                View project case studies
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {contractorServices.map((service, i) => {
            const ServiceIcon = service.icon;
            return <Card key={service.title} data-index={i} className="group relative border-border bg-card shadow-xs hover:shadow-md hover:border-primary/50 transition-[border-color,box-shadow] flex flex-col justify-between">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <div className="size-10 rounded-lg bg-muted text-foreground flex items-center justify-center group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                        <ServiceIcon className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-xs">
                        Commercial grade
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mt-2">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <Button asChild variant="ghost" size="sm" className="p-0 text-primary font-medium hover:bg-transparent group-hover:translate-x-1 transition-transform">
                      <Link to={service.to}>
                        <span>Explore service details</span>
                        <ArrowRight className="size-4 ml-1.5" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Geographic Coverage Block */}
        <div className="rounded-2xl bg-secondary text-secondary-foreground p-6 sm:p-10 shadow-md">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="size-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Operational Coverage
                </span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-secondary-foreground font-default mb-3">
                Regional service across the South East
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Operating with established transport logistics, rapid response capabilities for site adaptations, and consistent crew management across all core areas.
              </p>

              <div className="flex flex-wrap gap-2 pt-5">
                {coverageAreas.map((area, i) => <Badge key={area} data-index={i} variant="outline" className="bg-card text-card-foreground border-border text-xs px-3 py-1 font-medium">
                    {area}
                  </Badge>)}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 justify-center lg:items-end">
              <Button asChild size="lg" className="w-full sm:w-auto lg:w-full">
                <Link to="/AreasWeCoverPage">View full coverage map</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto lg:w-full bg-transparent text-secondary-foreground border-border hover:bg-card hover:text-card-foreground">
                <Link to="/ContactRequestQuotePage">Send schedule of works</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}